import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieCard from '@/components/feature/MovieCard';
import { allMovies } from '@/mocks/movies';
import { useState, useMemo } from 'react';

const categoryMap: Record<string, { label: string; subcategories: string[] }> = {
  movie: {
    label: '电影',
    subcategories: ['全部', '科幻', '历史', '悬疑', '犯罪', '动画', '奇幻'],
  },
  tv: {
    label: '电视剧',
    subcategories: ['全部', '犯罪', '科幻', '年代', '古装', '悬疑'],
  },
  variety: {
    label: '综艺',
    subcategories: ['全部', '户外真人秀', '慢综艺', '音乐竞演'],
  },
  anime: {
    label: '动漫',
    subcategories: ['全部', '热血', '仙侠'],
  },
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSub, setActiveSub] = useState('全部');

  const category = categoryMap[slug || ''] || { label: '未知分类', subcategories: ['全部'] };
  const movies = useMemo(() => {
    let filtered = allMovies.filter((m) => m.category === slug);
    if (activeSub !== '全部') {
      filtered = filtered.filter((m) => m.subcategory === activeSub);
    }
    return filtered;
  }, [slug, activeSub]);

  const schemaOrg = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `窝窝影院 - ${category.label}分类`,
    description: `窝窝影院${category.label}频道，提供最新${category.label}资源，免费在线观看，高清播放不缓冲，每日更新。`,
    url: `https://www.wowocinema.com/category/${slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '窝窝影院',
      url: 'https://www.wowocinema.com/',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: `窝窝影院${category.label}频道有什么内容？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `窝窝影院${category.label}频道提供最新最全的${category.label}资源，包含${category.subcategories.slice(1).join('、')}等多种类型，全部免费在线观看，高清画质不缓冲。`,
        },
      },
      {
        '@type': 'Question',
        name: `如何在窝窝影院免费观看${category.label}？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: '在窝窝影院，您只需点击任意影片即可免费在线观看，无需注册，无需付费，全程无广告，高清流畅播放。',
        },
      },
    ],
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(schemaOrg)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      <Navbar />
      <ScrollToTop />
      <main className="pt-16">
        <div className="bg-background-100">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: category.label },
              ]}
            />
          </div>
        </div>

        <section className="w-full px-4 md:px-6 py-8">
          <div className="max-w-7xl mx-auto">
            <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-2">
              {category.label}
            </h1>
            <p className="text-sm text-foreground-500 mb-6">
              窝窝影院{category.label}频道 — 最新{category.label}免费在线观看，高清流畅不缓冲
            </p>

            <div className="flex items-center gap-2 flex-wrap mb-8">
              {category.subcategories.map((sub) => (
                <button
                  key={sub}
                  onClick={() => setActiveSub(sub)}
                  className={`px-4 py-1.5 text-sm rounded-full whitespace-nowrap cursor-pointer transition-all duration-200 ${
                    activeSub === sub
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-900'
                  }`}
                >
                  {sub}
                </button>
              ))}
            </div>

            {movies.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto rounded-full bg-background-100 flex items-center justify-center mb-4">
                  <i className="ri-emotion-sad-line text-foreground-400 text-2xl"></i>
                </div>
                <p className="text-foreground-500">暂无相关内容</p>
              </div>
            ) : (
              <div className="space-y-1">
                {movies.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} variant="landscape" />
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}